import { Injectable, InjectionToken, inject } from '@angular/core';
import { generateTransferReferenceId } from '@pedix-workspace/orders';
import { DigitalPaymentConciliationType, ResolvedTransferPayer } from '@pedix-workspace/utils';

export type GenerateCVUParams = {
  establishmentId: string;
  customerName: string;
  amount?: number;
  referenceId?: string;
  payerDocument?: string;
};

export type BankTransferErrorCode =
  | 'invalid_document'
  | 'payer_not_found'
  | 'payer_not_changeable'
  | 'invalid_payer_token';

export class BankTransferRequestError extends Error {
  readonly code: BankTransferErrorCode | null;

  constructor(message: string, code: BankTransferErrorCode | null) {
    super(message);
    this.code = code;
  }
}

export type UpdateAmountParams = {
  referenceId: string;
  amount: number;
  establishmentId: string;
};

type GenerateCVUApiResponse = {
  cvu: string;
  accountHolder: string;
  alias?: string;
  provider: 'chytapay' | 'cucuru';
  conciliationType?: DigitalPaymentConciliationType;
  payer?: ResolvedTransferPayer;
  payerToken?: string;
};

type UpdatePayerApiResponse = {
  cvu: string;
  payer: ResolvedTransferPayer;
};

export type GenerateCVUResponse = {
  cvu: string;
  accountHolder: string;
  alias?: string;
  provider: 'chytapay' | 'cucuru';
  referenceId: string;
  conciliationType: DigitalPaymentConciliationType;
  payerDocument?: string;
  payer?: ResolvedTransferPayer;
  payerToken?: string;
};

/**
 * Injection token for the bank transfers API URL.
 * Must be provided by the application.
 */
export const BANK_TRANSFERS_API_URL = new InjectionToken<string>('BANK_TRANSFERS_API_URL');

/**
 * Service to handle bank transfer CVU generation for the checkout flow.
 * Calls the bank-transfers API to generate a dynamic CVU using a transfer reference ID.
 *
 * Note: This service uses `referenceId` instead of `orderId` to decouple
 * transfer CVU generation from order creation.
 */
@Injectable()
export class BankTransferService {
  private readonly bankTransfersApiUrl = inject(BANK_TRANSFERS_API_URL, { optional: true });
  private readonly REQUEST_TIMEOUT_MS = 30000;

  /**
   * Cached CVU keyed by establishmentId so a subsidiary switch never reuses
   * the previous sucursal's alias. Multiple subsidiaries can coexist so
   * navigating back to a previously visited one reuses its CVU.
   */
  private readonly cachedCvuByEstablishment = new Map<string, GenerateCVUResponse>();
  private readonly pendingRequestByEstablishment = new Map<string, Promise<GenerateCVUResponse>>();

  getCachedCvuData(establishmentId: string): GenerateCVUResponse | null {
    return this.cachedCvuByEstablishment.get(establishmentId) ?? null;
  }

  clearCache(): void {
    this.cachedCvuByEstablishment.clear();
  }

  /**
   * Generates a CVU for a bank transfer payment, or returns cached data if available.
   *
   * The CVU is cached to prevent creating orphan payment requests when the user
   * navigates between payment methods during checkout.
   */
  async generateCVU(params: GenerateCVUParams): Promise<GenerateCVUResponse> {
    const { establishmentId } = params;
    const pendingRequest = this.pendingRequestByEstablishment.get(establishmentId);
    const request = (pendingRequest ?? Promise.resolve())
      .catch(() => null)
      .then(() => this.generateOrReuseCVU(params));

    this.pendingRequestByEstablishment.set(establishmentId, request);

    try {
      return await request;
    } finally {
      if (this.pendingRequestByEstablishment.get(establishmentId) === request) {
        this.pendingRequestByEstablishment.delete(establishmentId);
      }
    }
  }

  private async generateOrReuseCVU(params: GenerateCVUParams): Promise<GenerateCVUResponse> {
    const cached = this.getCachedCvuData(params.establishmentId);
    if (cached && isCachedForReference(cached, params.referenceId)) {
      return needsPayerChange(cached, params.payerDocument)
        ? this.updatePayer(params.establishmentId, cached, params.payerDocument)
        : cached;
    }

    const referenceId = params.referenceId ?? generateTransferReferenceId();

    const data = await this.request<GenerateCVUApiResponse>('/generate-cvu', 'POST', {
      referenceId,
      establishmentId: params.establishmentId,
      customerName: params.customerName,
      amount: params.amount,
      payerDocument: params.payerDocument,
    });

    const result: GenerateCVUResponse = {
      cvu: data.cvu,
      accountHolder: data.accountHolder,
      alias: data.alias,
      provider: data.provider,
      referenceId,
      conciliationType: data.conciliationType ?? 'cvu',
      payerDocument: data.payer ? params.payerDocument : undefined,
      payer: data.payer,
      payerToken: data.payerToken,
    };

    this.cachedCvuByEstablishment.set(params.establishmentId, result);

    return result;
  }

  /**
   * Updates the amount of an existing payment request.
   * Uses the transfer reference ID.
   *
   * @param params - Parameters for amount update
   */
  async updateAmount(params: UpdateAmountParams): Promise<void> {
    await this.request<unknown>('/update-amount', 'PATCH', {
      referenceId: params.referenceId,
      amount: params.amount,
      establishmentId: params.establishmentId,
    });
  }

  private async updatePayer(
    establishmentId: string,
    cached: GenerateCVUResponse,
    payerDocument: string,
  ): Promise<GenerateCVUResponse> {
    const data = await this.request<UpdatePayerApiResponse>('/payer', 'PATCH', {
      referenceId: cached.referenceId,
      establishmentId,
      payerDocument,
      payerToken: cached.payerToken,
    });

    const result: GenerateCVUResponse = {
      ...cached,
      cvu: data.cvu,
      payerDocument,
      payer: data.payer,
    };

    this.cachedCvuByEstablishment.set(establishmentId, result);

    return result;
  }

  private async request<T>(path: string, method: 'POST' | 'PATCH', body: object): Promise<T> {
    if (!this.bankTransfersApiUrl) {
      throw new Error('BANK_TRANSFERS_API_URL not provided');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(`${this.bankTransfersApiUrl}${path}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new BankTransferRequestError(
          error.message || `Error calling ${path}`,
          error.error ?? null,
        );
      }

      return await response.json().catch(() => null);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('La solicitud tardó demasiado. Por favor, intenta nuevamente.');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Checks if a transfer integration is available for the given integrations.
   */
  hasTransferIntegration(integrations: { types: string[] }[]): boolean {
    return integrations.some(i => i.types.includes('bank-transfer'));
  }

  /**
   * Gets the provider of the transfer integration.
   */
  getTransferProvider(
    integrations: { types: string[]; provider: string }[],
  ): 'chytapay' | 'cucuru' | null {
    const integration = integrations.find(i => i.types.includes('bank-transfer'));
    if (!integration) return null;

    const provider = integration.provider;
    if (provider === 'chytapay' || provider === 'cucuru') {
      return provider;
    }
    return null;
  }
}

export function isCachedForReference(
  cached: GenerateCVUResponse,
  referenceId: string | undefined,
): boolean {
  return referenceId === undefined || cached.referenceId === referenceId;
}

function needsPayerChange(
  cached: GenerateCVUResponse,
  payerDocument: string | undefined,
): payerDocument is string {
  return (
    cached.conciliationType === 'cuil' &&
    payerDocument !== undefined &&
    payerDocument !== cached.payerDocument &&
    payerDocument !== cached.payer?.taxId
  );
}
